import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
  selectSessionFilter,
  selectSessionFilterContactIds,
  selectSessionFilterDateRange,
  selectSessionFilterMeetingKind,
  selectSessionFilterMeetingSources,
  selectSessionFilterMeetingTemplateIds,
  selectSessionFilterPage,
  selectSessionFilterPageSize,
  selectSessionFilterTags,
  selectSessionFilterUserIds,
} from '../../../../../store/sessionFilter/sessionFilter'
import { ReactComponent as ZocksIcon } from '../../../../../assets/ZocksIcon.svg'
import { Button, ButtonProps } from '../../../../../components/Button/Button'
import { MeetingWorkflowsMenu } from '../../../../../components/CustomWorkflows/MeetingWorkflowsMenu'
import { useMeetingWorkflowLauncher } from '../../../../../components/CustomWorkflows/useMeetingWorkflowLauncher'
import { workflowMenuLabel } from '../../../../../components/CustomWorkflows/workflowMenuLabel'
import { useHeaderMeetingTemplateSelector } from '../../MeetingSummary/Header/hooks'
import React, { type PropsWithChildren, type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  CustomDropdownItemOption,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectOption,
} from '../../../../../components/SelectDropdown/SelectDropdown'
import { setSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { TagElement } from '../../../../../components/Tag/Tag'
import { DeprecatedTemplateIndicator } from '../../../../../components/DeprecatedTemplateIndicator/DeprecatedTemplateIndicator'
import { getMeetingTemplateOptionFlags } from '../../../../../common/meetingTemplate'
import { Offcanvas } from 'react-bootstrap'
import { MeetingTemplateInfo } from '../../MeetingSummary/MeetingTemplateInfo/MeetingTemplateInfo'
import { DeprecatedMeetingTemplateInfo } from '../../MeetingSummary/DeprecatedMeetingTemplateInfo/DeprecatedMeetingTemplateInfo'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getRoomTrackingDetails } from '../../tracking'
import { selectRoomSession } from '../../../../../store/meetingSummary/roomSession'

export const MeetingPreparationHeader = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const selectedUserIds = useAppSelector(selectSessionFilterUserIds)
  const selectedContactIds = useAppSelector(selectSessionFilterContactIds)
  const selectedDateRange = useAppSelector(selectSessionFilterDateRange)
  const selectedMeetingTypeIds = useAppSelector(selectSessionFilterMeetingTemplateIds)
  const selectedFilterTags = useAppSelector(selectSessionFilterTags)
  const selectedMeetingKind = useAppSelector(selectSessionFilterMeetingKind)
  const selectedMeetingSources = useAppSelector(selectSessionFilterMeetingSources)
  const selectedFilter = useAppSelector(selectSessionFilter)
  const selectedPage = useAppSelector(selectSessionFilterPage)
  const selectedPageSize = useAppSelector(selectSessionFilterPageSize)

  const handleBackButtonClicked = () => {
    const searchParams = {
      ...(selectedFilter && { filterId: selectedFilter.id }),
      ...(selectedPage && { page: selectedPage.toString() }),
      ...(selectedPageSize && { pageSize: selectedPageSize.toString() }),
      ...(selectedUserIds && !selectedFilter && { userId: selectedUserIds.join(',') }),
      ...(selectedContactIds && !selectedFilter && { contactId: selectedContactIds.join(',') }),
      ...(selectedMeetingKind && !selectedFilter && { meetingKind: selectedMeetingKind }),
      ...(selectedMeetingSources.length && !selectedFilter && { meetingSources: JSON.stringify(selectedMeetingSources) }),
      ...(selectedDateRange && !selectedFilter && { dateRange: JSON.stringify(selectedDateRange) }),
      ...(selectedMeetingTypeIds.length && !selectedFilter && { meetingTemplates: JSON.stringify(selectedMeetingTypeIds) }),
      ...(selectedFilterTags.length && !selectedFilter && { tags: JSON.stringify(selectedFilterTags) }),
    }
    navigate({
      pathname: `/workspace/${pathname.includes('live-meetings') ? 'live-meetings' : 'upcoming'}`,
      search: createSearchParams(searchParams).toString(),
    })
  }

  return (
    <div className='d-flex flex-column pe-3'>
      <div className='d-flex zocks-pl-2.0 justify-content-between align-items-center'>
        <div className='d-flex align-items-center zocks-gap-2'>
          <div className='d-lg-none'>
            <ZocksIcon />
          </div>
          <div className='d-none align-items-center d-sm-flex zocks-gap-1.25'>
            <Button variant='link' className='back-button' onClick={handleBackButtonClicked}>
              <i className='bi bi-chevron-left' />
            </Button>
          </div>
        </div>
        <div className='d-none d-sm-flex align-items-center gap-3'>
          <MeetingWorkflowsMenu />
          <MeetingTemplateSelector />
        </div>
        <div className='d-sm-none'>
          <MobileMenu />
        </div>
      </div>
      <MeetingTemplateInfo />
      <DeprecatedMeetingTemplateInfo variant='preparation' />
    </div>
  )
}

const MeetingTemplateSelector = () => {
  const dispatch = useAppDispatch()
  const roomSession = useAppSelector(selectRoomSession)
  const { tracked } = useTracking()

  const { meetingTemplates, sessionMeetingTemplate, selectedMeetingTemplate } = useHeaderMeetingTemplateSelector('meetingPreparation')

  const currentIsDeprecated = sessionMeetingTemplate?.isDeprecated === true

  const options = useMemo(() => {
    return meetingTemplates
      .filter(({ id, isActive }) => !!isActive || sessionMeetingTemplate?.id === id)
      .map(({ id, name }) => ({ id, label: name }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [meetingTemplates, sessionMeetingTemplate])

  const _handleChangeMeetingTemplate = (option: SelectOption) => {
    const { id: meetingTemplateId } = option
    dispatch(setSelectedMeetingTemplate(meetingTemplateId))
  }

  const handleChangeMeetingTemplate = tracked(
    _handleChangeMeetingTemplate,
    { eventName: TrackEvent.ROOM_SESSION_MEETING_TYPE_CHANGED, eventDetails: getRoomTrackingDetails(roomSession) },
    {
      eventDetailsExtractor: ({ id: selectedMeetingTemplateId }: SelectOption) => {
        return {
          selectedMeetingTemplateId,
          source: 'meeting-prep',
        }
      },
    },
  )

  const SessionMeetingTemplateDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick }) => {
      const deprecatedLabel = meetingTemplates.find((mt) => mt.id === optionId)?.label
      const { isNewVersion, isCurrent } = getMeetingTemplateOptionFlags({
        optionId,
        currentTemplate: sessionMeetingTemplate,
        allTemplates: meetingTemplates,
      })

      return (
        <CustomDropdownItemOption role='button' onClick={onClick} className='d-flex justify-content-between align-items-center gap-2'>
          <span>
            {optionLabel}
            {deprecatedLabel ? <span className='zocks-text-gray-600'> {deprecatedLabel}</span> : null}
          </span>
          <span className='d-flex gap-1 align-items-center'>
            {isNewVersion ? <TagElement className='meeting-template-new-version-tag'>New version</TagElement> : null}
            {isCurrent ? <span className='zocks-text-primary fw-medium paragraph-small'>Current</span> : null}
          </span>
        </CustomDropdownItemOption>
      )
    },
    [meetingTemplates, sessionMeetingTemplate],
  )

  return (
    <>
      <div className='header-dropdown-container d-none d-xl-block position-relative'>
        <SelectDropdown
          className='zocks-py-0.375 zocks-px-0.75'
          options={options}
          icon='bi-columns-gap'
          label={selectedMeetingTemplate?.name}
          labelClassName='pe-2'
          onOptionSelected={handleChangeMeetingTemplate}
          DropDownItemOptionComponent={SessionMeetingTemplateDropdownItemOption}
        />
        {currentIsDeprecated ? <DeprecatedTemplateIndicator variant='dot' title='Meeting type has a new version available' /> : null}
      </div>
      <div className='header-dropdown-container d-none d-sm-block d-xl-none'>
        <SelectDropdown
          className='zocks-py-0.375 zocks-px-0.75'
          options={options}
          icon='bi-columns-gap'
          iconOnly
          onOptionSelected={handleChangeMeetingTemplate}
          DropDownItemOptionComponent={SessionMeetingTemplateDropdownItemOption}
        />
      </div>
    </>
  )
}

const MobileMenu = () => {
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false)

  const hideMenu = () => setShowMobileMenu(false)

  const workflowLauncher = useMeetingWorkflowLauncher()

  return (
    <>
      {workflowLauncher.runModal}
      <Button variant='borderless' onClick={() => setShowMobileMenu((showMobileMenu) => !showMobileMenu)}>
        <i className='bi bi-list' />
      </Button>
      <Offcanvas className='h-100 mt-10' show={showMobileMenu} placement={'top'} onHide={hideMenu} backdropClassName='d-none' scroll>
        <div className='d-flex flex-column p-3'>
          <div className='d-flex align-items-center justify-content-end'>
            <Button variant='link' className='button-link-small text-decoration-none py-3' onClick={hideMenu}>
              <i className='bi bi-x-lg' /> Close
            </Button>
          </div>
          <MobileMeetingTemplateSelector />
          {workflowLauncher.enabled
            ? workflowLauncher.workflows.map((workflow) => (
                <MobileMenuItem
                  key={workflow.id}
                  onClick={() => {
                    workflowLauncher.launch(workflow.id)
                    hideMenu()
                  }}
                >
                  <div className='d-flex gap-2 align-items-center'>
                    <i className='bi bi-stars' />
                    {workflowMenuLabel(workflow)}
                  </div>
                </MobileMenuItem>
              ))
            : null}
        </div>
      </Offcanvas>
    </>
  )
}

const MobileMeetingTemplateSelector: React.FC = () => {
  const dispatch = useAppDispatch()

  const { meetingTemplates, sessionMeetingTemplate, selectedMeetingTemplate } = useHeaderMeetingTemplateSelector('meetingPreparation')

  const handleSelectMeetingTemplate = (meetingTemplateId: string) => {
    dispatch(setSelectedMeetingTemplate(meetingTemplateId))
  }

  const menuItems = (
    <>
      {meetingTemplates.map((meetingTemplate) => {
        const { isNewVersion, isCurrent } = getMeetingTemplateOptionFlags({
          optionId: meetingTemplate.id,
          currentTemplate: sessionMeetingTemplate,
          allTemplates: meetingTemplates,
        })
        return (
          <MobileMenuItem key={meetingTemplate.id} onClick={() => handleSelectMeetingTemplate(meetingTemplate.id)}>
            <MeetingTemplateMenuItemContent
              name={meetingTemplate.name}
              deprecatedLabel={meetingTemplate.label}
              isNewVersion={isNewVersion}
              isCurrent={isCurrent}
            />
          </MobileMenuItem>
        )
      })}
    </>
  )

  return (
    <ExpandableMobileMenuButton menuItems={menuItems}>
      <div className='d-flex gap-2 align-items-center'>
        <i className='bi bi-columns-gap' />
        {selectedMeetingTemplate?.name ?? 'Change meeting type'}
      </div>
    </ExpandableMobileMenuButton>
  )
}

interface ExpandableMobileMenuButtonProps extends ButtonProps {
  menuItems: ReactNode
}

const ExpandableMobileMenuButton: React.FC<PropsWithChildren<ExpandableMobileMenuButtonProps>> = ({ menuItems, children, onClick, ...rest }) => {
  const [expanded, setExpanded] = useState<boolean>(false)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setExpanded((expanded) => !expanded)
    onClick?.(e)
  }

  return (
    <div className='d-flex flex-column mobile-menu-item'>
      <Button variant='borderless' className='justify-content-between py-3 zocks-px-0.75' onClick={handleClick} {...rest}>
        <div className='d-flex gap-2 align-items-center'>{children}</div>
        {expanded ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
      </Button>
      {expanded ? <div className='d-flex flex-column gap-2'>{menuItems}</div> : null}
    </div>
  )
}

const MobileMenuItem: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...rest }) => {
  return (
    <div className={`d-flex py-2 pe-2 ps-4 paragraph-small expandable-menu-item align-items-center gap-2 ${className}`} {...rest}>
      {children}
    </div>
  )
}

interface MeetingTemplateMenuItemContentProps {
  name: string
  deprecatedLabel?: string | null
  isNewVersion: boolean
  isCurrent: boolean
}

const MeetingTemplateMenuItemContent: React.FC<MeetingTemplateMenuItemContentProps> = ({ name, deprecatedLabel, isNewVersion, isCurrent }) => (
  <div className='d-flex justify-content-between align-items-center w-100 gap-2'>
    <span>
      {name}
      {deprecatedLabel ? <span className='zocks-text-gray-600'> {deprecatedLabel}</span> : null}
    </span>
    <span className='d-flex gap-1 align-items-center'>
      {isNewVersion ? <TagElement className='meeting-template-new-version-tag'>New version</TagElement> : null}
      {isCurrent ? <span className='zocks-text-primary fw-medium paragraph-small'>Current</span> : null}
    </span>
  </div>
)
